// Cliente HTTP centralizado: añade el token, parsea errores y gestiona sesión caducada.
import { API_BASE_URL } from "config";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const getToken = () => localStorage.getItem("token");

// FastAPI devuelve los errores de validación (422) como una lista de objetos
// {loc, msg, type}, no como texto. Sin esto, el usuario veía el JSON crudo.
const formatValidationDetail = (detail) => {
  if (!Array.isArray(detail)) return null;
  const mensajes = detail.map((item) => {
    const campo = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
    const msg = (item?.msg || "Dato inválido").replace(/^Value error,\s*/i, "");
    return campo && campo !== "body" ? `${campo}: ${msg}` : msg;
  });
  return mensajes.join(" · ");
};

/**
 * apiFetch("/api/equipos") -> JSON parseado.
 * Lanza ApiError con el detail del backend si la respuesta no es OK.
 * En 401 limpia el token y redirige al login.
 */
export async function apiFetch(path, { method = "GET", body, params } = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });
  }

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    // fetch() lanza un TypeError genérico ("Failed to fetch") tanto si no hay
    // conexión como si el servidor no responde (p.ej. arrancando tras estar
    // inactivo). Sustituimos ese mensaje críptico por uno accionable.
    throw new ApiError(
      "No se pudo conectar con el servidor. Comprueba tu conexión a internet. Si la app llevaba un rato sin usarse, el servidor puede tardar unos segundos en arrancar: espera un momento y vuelve a intentarlo.",
      0
    );
  }

  if (response.status === 401) {
    localStorage.removeItem("token");
    if (!window.location.pathname.startsWith("/authentication")) {
      window.location.href = "/authentication/sign-in";
    }
    throw new ApiError("Sesión caducada. Vuelve a iniciar sesión.", 401);
  }

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  if (!response.ok) {
    // La mayoría de endpoints usan {detail: "..."} (formato FastAPI), pero
    // algunos (p.ej. /externas/clasificacion) devuelven {message: "..."} a
    // mano — sin este fallback, esos casos caían en el genérico "Error 400".
    const detail =
      (data &&
        (typeof data.detail === "string"
          ? data.detail
          : formatValidationDetail(data.detail) || data.message || JSON.stringify(data.detail))) ||
      `Error ${response.status}`;
    throw new ApiError(detail, response.status);
  }

  return data;
}

export default apiFetch;
